import React, { useState } from "react";
import { Listing, ListingStatus } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Edit, Trash2, Eye } from "lucide-react"; // Import Eye
import { Link } from "react-router-dom";
import {
  publishUserListing, // Keep for mutationFn reference if needed, but use prop
  deleteUserListing, // Keep for mutationFn reference if needed, but use prop
} from "@/lib/api/listings/userListingsApi";
import { UserRole } from "@/lib/types"; // Import UserRole
import { Switch } from "@/components/ui/switch"; // Import Switch

interface ListingTableProps {
  listings: Listing[];
  userRole?: UserRole; // Add userRole prop
  onPublish: (id: number) => void; // Add onPublish prop
  onDelete: (id: number) => void; // Add onDelete prop
  onFeatureToggle: (id: number) => void; // Add onFeatureToggle prop
  isPublishing: boolean; // Add isPublishing prop
  isDeleting: boolean; // Add isDeleting prop
  isFeatureToggling: boolean; // Add isFeatureToggling prop
}

const getStatusVariant = (status: ListingStatus) => {
  switch (status) {
    case "published":
    case "active":
      return "default";
    case "pending":
      return "secondary";
    case "draft":
      return "outline";
    case "archived":
    case "inactive":
      return "destructive";
    default:
      return "secondary";
  }
};

const ListingTable: React.FC<ListingTableProps> = ({
  listings,
  userRole,
  onPublish,
  onDelete,
  onFeatureToggle, // Destructure onFeatureToggle
  isPublishing, // Destructure isPublishing
  isDeleting, // Destructure isDeleting
  isFeatureToggling, // Destructure isFeatureToggling
}) => {
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState<number | null>(
    null
  );

  const handlePublishClick = (listingId: number) => {
    setSelectedListingId(listingId);
    setIsPublishConfirmOpen(true);
  };

  const handleConfirmPublish = () => {
    if (selectedListingId) {
      onPublish(selectedListingId);
    }
  };

  const handleDeleteClick = (listingId: number) => {
    setSelectedListingId(listingId);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedListingId) {
      onDelete(selectedListingId);
    }
  };

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-full bg-white">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">Image</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Featured</TableHead> {/* New TableHead for Featured */}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                No listings found.
              </TableCell>
            </TableRow>
          ) : (
            listings.map((listing) => (
              <TableRow key={listing.id}>
                <TableCell>
                  <img
                    src={
                      listing.images?.[0]?.url || "/placeholder-property.avif"
                    }
                    alt={listing.title}
                    className="w-16 h-16 object-cover rounded-md"
                  />
                </TableCell>
                <TableCell className="font-medium">{listing.title}</TableCell>
                <TableCell>
                  {new Intl.NumberFormat("en-LK", {
                    style: "currency",
                    currency: listing.currency || "LKR",
                  }).format(listing.price)}
                </TableCell>
                <TableCell>
                  {format(new Date(listing.created_at), "PPP")}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(listing.status)}>
                    {listing.status}
                  </Badge>
                  {listing.status === "pending" &&
                    userRole && // Ensure userRole is not undefined
                    (userRole === "admin" || userRole === "agent") && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 h-7"
                        onClick={() => handlePublishClick(listing.id)}
                        disabled={
                          isPublishing && selectedListingId === listing.id
                        }
                      >
                        {isPublishing && selectedListingId === listing.id
                          ? "Publishing..."
                          : "Publish"}
                      </Button>
                    )}
                </TableCell>
                <TableCell>
                  {/* New TableCell for Featured */}
                  <Switch
                    checked={!!listing.is_featured} // Convert to boolean
                    onCheckedChange={() => onFeatureToggle(listing.id)}
                    aria-label="Toggle featured status"
                    disabled={
                      isFeatureToggling && selectedListingId === listing.id
                    }
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      asChild
                      className="h-8 w-8"
                    >
                      <Link to={`/property/${listing.id}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      asChild
                      className="h-8 w-8"
                    >
                      <Link
                        to={`/dashboard/add-listing/edit/${listing.id}/step/6`}
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDeleteClick(listing.id)}
                      disabled={isDeleting && selectedListingId === listing.id}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Publish Confirmation Dialog */}
      <AlertDialog
        open={isPublishConfirmOpen}
        onOpenChange={setIsPublishConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Publish</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to publish this listing? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPublishing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPublish}
              disabled={isPublishing}
            >
              {isPublishing ? "Publishing..." : "Yes, Publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this listing? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ListingTable;
